import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Tag, Popconfirm, Card } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import { userService } from '../../services/userService';
import dayjs from 'dayjs';

const { Option } = Select;

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [editingUser, setEditingUser] = useState(null);
  const [changingPasswordUser, setChangingPasswordUser] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await userService.getAllUsers();
      console.log('users', res.data);
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
      message.error('เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    console.log('editting...', record);
    record.role_id = record.role_name
    setEditingUser(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await userService.deleteUser(id);
      message.success('ลบผู้ใช้สำเร็จ');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error('เกิดข้อผิดพลาดในการลบผู้ใช้');
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (editingUser) {
        await userService.updateUser(editingUser.id, values);
        message.success('แก้ไขข้อมูลผู้ใช้สำเร็จ');
      } else {
        await userService.createUser(values);
        message.success('เพิ่มผู้ใช้ใหม่สำเร็จ');
      }

      setModalVisible(false);
      form.resetFields();
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      message.error('เกิดข้อผิดพลาดในการบันทึกข้อมูลผู้ใช้');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = (user) => {
    setChangingPasswordUser(user);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handlePasswordSubmit = async (values) => {
    setLoading(true);
    try {
      await userService.changePassword(changingPasswordUser.id, values);
      message.success('เปลี่ยนรหัสผ่านสำเร็จ');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error) {
      console.error('Error changing password:', error);
      message.error('เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role) => {
    const roleColors = {
      'super_admin': 'red',
      'admin': 'orange',
      'manager': 'blue',
      'sale': 'green',
      'accounting': 'purple',
      'support': 'cyan'
    };
    return roleColors[role] || 'default';
  };

  const getRoleText = (role) => {
    const roleTexts = {
      'super_admin': 'เจ้าของ (Super Admin)',
      'admin': 'ผู้ดูแลระบบ (Admin)',
      'manager': 'ผู้จัดการ (Manager)',
      'sale': 'ฝ่ายขาย (Sale)',
      'accounting': 'ฝ่ายบัญชี (Accounting)',
      'support': 'ฝ่ายบริการ (Support)'
    };
    return roleTexts[role] || role;
  };

  const columns = [
    {
      title: 'ชื่อ',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'อีเมล',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'เบอร์โทร',
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: 'ตำแหน่ง',
      dataIndex: 'role_name',
      key: 'role',
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {getRoleText(role)}
        </Tag>
      ),
      filters: [
        { text: 'เจ้าของ', value: 'super_admin' },
        { text: 'ผู้ดูแลระบบ', value: 'admin' },
        { text: 'ผู้จัดการ', value: 'manager' },
        { text: 'ฝ่ายขาย', value: 'sale' },
        { text: 'ฝ่ายบัญชี', value: 'accounting' },
        { text: 'ฝ่ายบริการ', value: 'support' }
      ],
      onFilter: (value, record) => record.role === value
    },
    {
      title: 'ที่อยู่',
      dataIndex: 'address',
      key: 'address',
      render: (text) => text && text.length > 30 ? `${text.substring(0, 30)}...` : text || '-'
    },
        {
      title: 'เข้าระบบล่าสุด',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (text) => dayjs(text).format('DD/MM/YYYY HH:mm:ss')
    },
    {
      title: 'การจัดการ',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            แก้ไข
          </Button>
          <Button
            type="default"
            size="small"
            icon={<LockOutlined />}
            onClick={() => handleChangePassword(record)}
          >
            เปลี่ยนรหัส
          </Button>
          <Popconfirm
            title="คุณต้องการลบผู้ใช้คนนี้ใช่หรือไม่?"
            onConfirm={() => handleDelete(record.id)}
            okText="ใช่"
            cancelText="ไม่"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              ลบ
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <UserOutlined style={{ marginRight: 8 }} />
            จัดการผู้ใช้ระบบ
          </div>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            เพิ่มผู้ใช้ใหม่
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} จาก ${total} รายการ`
          }}
        />
      </Card>

      {/* Modal for Add/Edit User */}
      <Modal
        title={editingUser ? 'แก้ไขข้อมูลผู้ใช้' : 'เพิ่มผู้ใช้ใหม่'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="ชื่อ-นามสกุล"
            rules={[{ required: true, message: 'กรุณากรอกชื่อ-นามสกุล' }]}
          >
            <Input placeholder="ชื่อ-นามสกุล" />
          </Form.Item>

          <Form.Item
            name="email"
            label="อีเมล"
            rules={[
              { required: true, message: 'กรุณากรอกอีเมล' },
              { type: 'email', message: 'รูปแบบอีเมลไม่ถูกต้อง' }
            ]}
          >
            <Input placeholder="อีเมล" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="เบอร์โทรศัพท์"
            rules={[{ required: true, message: 'กรุณากรอกเบอร์โทรศัพท์' }]}
          >
            <Input placeholder="เบอร์โทรศัพท์" />
          </Form.Item>

          <Form.Item
            name="role_id"
            label="ตำแหน่งหน้าที่"
            rules={[{ required: true, message: 'กรุณาเลือกตำแหน่งหน้าที่' }]}
          >
            <Select placeholder="เลือกตำแหน่งหน้าที่">
              <Option value="1">เจ้าของ (Super Admin)</Option>
              <Option value="2">ผู้ดูแลระบบ (Admin)</Option>
              <Option value="3">ผู้จัดการ (Manager)</Option>
              <Option value="4">ฝ่ายขาย (Sale)</Option>
              <Option value="5">ฝ่ายบัญชี (Accounting)</Option>
              <Option value="6">ฝ่ายบริการ (Support)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="address"
            label="ที่อยู่"
            rules={[{ required: true, message: 'กรุณากรอกที่อยู่' }]}
          >
            <Input.TextArea rows={3} placeholder="ที่อยู่" />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="รหัสผ่าน"
              rules={[
                { required: true, message: 'กรุณากรอกรหัสผ่าน' },
                { min: 6, message: 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร' }
              ]}
            >
              <Input.Password placeholder="รหัสผ่าน" />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={() => setModalVisible(false)} style={{ marginRight: 8 }}>
              ยกเลิก
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingUser ? 'บันทึกการแก้ไข' : 'เพิ่มผู้ใช้'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal for Change Password */}
      <Modal
        title={`เปลี่ยนรหัสผ่าน: ${changingPasswordUser?.name}`}
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        footer={null}
        width={400}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordSubmit}
        >
          <Form.Item
            name="newPassword"
            label="รหัสผ่านใหม่"
            rules={[
              { required: true, message: 'กรุณากรอกรหัสผ่านใหม่' },
              { min: 6, message: 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร' }
            ]}
          >
            <Input.Password placeholder="รหัสผ่านใหม่" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="ยืนยันรหัสผ่าน"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'กรุณายืนยันรหัสผ่าน' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('รหัสผ่านไม่ตรงกัน'));
                }
              })
            ]}
          >
            <Input.Password placeholder="ยืนยันรหัสผ่าน" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={() => setPasswordModalVisible(false)} style={{ marginRight: 8 }}>
              ยกเลิก
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              เปลี่ยนรหัสผ่าน
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersPage;